import { useEffect, useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { AUDIT_RETENTION_DAYS } from '@/lib/utils';
import { db } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import { getOrg } from '@/lib/firestore';
import { resetPassword } from '@/lib/auth';
import { useT } from '@/lib/i18n';
import { LangSelect } from '@/components/LangSelect';
import type { Organization } from '@/types/models';
import { Loader } from '@/components/ui/loader';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label, labelClassName } from '@/components/ui/label';

export default function Settings() {
  const { user, profile, effectiveOrgId, refresh } = useAuth();
  const { t } = useT();
  const [name, setName] = useState(profile?.displayName ?? '');
  const [org, setOrg] = useState<Organization | null>(null);

  useEffect(() => {
    if (effectiveOrgId) getOrg(effectiveOrgId).then(setOrg);
  }, [effectiveOrgId]);

  const saveProfile = async () => {
    if (!user) return;
    await updateDoc(doc(db, 'users', user.uid), { displayName: name, updatedAt: serverTimestamp() });
    await refresh();
    toast.success(t('settings.profile_saved'));
  };

  if (!user || !profile) return <Loader fullscreen />;

  return (
    <div className="space-y-6">
      <h1 className="font-display text-2xl text-ink-900 dark:text-white">{t('settings.title')}</h1>

      <Card>
        <h2 className="font-display text-lg text-ink-900 dark:text-white">{t('settings.profile')}</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label>{t('common.fullname')}</Label>
            <Input className="mt-1" value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div>
            <Label>{t('common.email')}</Label>
            <Input className="mt-1" value={user.email ?? ''} disabled />
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <Button onClick={saveProfile}>{t('common.save')}</Button>
          <Button variant="secondary" onClick={async () => { await resetPassword(user.email!); toast.success(t('settings.email_sent')); }}>
            {t('settings.reset_pwd')}
          </Button>
        </div>
      </Card>

      <Card>
        <h2 className="font-display text-lg text-ink-900 dark:text-white">{t('settings.preferences')}</h2>
        <div className="mt-4 flex items-center justify-center gap-4">
          <span className="shrink-0 text-sm font-medium text-ink-700 dark:text-ink-200">{t('settings.language')}</span>
          <LangSelect className="w-48" />
        </div>
      </Card>

      {org && (
        <Card>
          <h2 className="font-display text-lg text-ink-900 dark:text-white">{t('settings.org')}</h2>
          <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 text-sm">
            <div><dt className={labelClassName()}>{t('common.name')}</dt><dd className="mt-1 text-ink-900 dark:text-white">{org.displayName}</dd></div>
            <div><dt className={labelClassName()}>{t('settings.org.plan')}</dt><dd className="mt-1 text-ink-900 dark:text-white">{t('settings.org.plan_value')}</dd></div>
            <div><dt className={labelClassName()}>{t('settings.org.contact')}</dt><dd className="mt-1 text-ink-900 dark:text-white">{org.primaryContact?.name ?? '—'} · {org.primaryContact?.email ?? '—'}</dd></div>
            <div><dt className={labelClassName()}>{t('settings.org.id')}</dt><dd className="mt-1 text-ink-700 dark:text-ink-200"><code>{org.id}</code></dd></div>
          </dl>
        </Card>
      )}

      <Card>
        <h2 className="font-display text-lg text-ink-900 dark:text-white">{t('settings.privacy')}</h2>
        <p className="mt-2 text-sm text-ink-500">{t('settings.privacy.text', { n: AUDIT_RETENTION_DAYS })}</p>
        <Link to="/help#data-collected" className="mt-2 inline-block text-sm font-medium text-brand-600 hover:underline dark:text-brand-300">{t('settings.privacy.more')}</Link>
      </Card>
    </div>
  );
}
